import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertTriangle, Search, Filter, ChevronDown, ChevronRight,
  Star, MessageSquare, Phone, Mail, Clock, User, Calendar,
  CheckCircle, XCircle, Loader2, ArrowLeft, FileText, ExternalLink
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import api from '../lib/api';

const STATUS_CONFIG = {
  NEW: { label: 'Nueva', color: 'bg-blue-500', textColor: 'text-blue-500' },
  IN_REVIEW: { label: 'En revisión', color: 'bg-yellow-500', textColor: 'text-yellow-500' },
  RESOLVED: { label: 'Resuelta', color: 'bg-green-500', textColor: 'text-green-500' },
  CLOSED: { label: 'Cerrada', color: 'bg-gray-500', textColor: 'text-gray-500' }
};

const PRIORITY_CONFIG = {
  HIGH: { label: 'Alta', color: 'bg-red-500', textColor: 'text-red-500', icon: '🔴' },
  MEDIUM: { label: 'Media', color: 'bg-yellow-500', textColor: 'text-yellow-500', icon: '🟡' },
  LOW: { label: 'Baja', color: 'bg-green-500', textColor: 'text-green-500', icon: '🟢' }
};

const IncidentsManager = () => {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [selectedIncident, setSelectedIncident] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Note form
  const [newNote, setNewNote] = useState('');
  const [updating, setUpdating] = useState(false);
  
  const fetchIncidents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (priorityFilter) params.append('priority', priorityFilter);
      if (searchQuery) params.append('search', searchQuery);
      params.append('skip', page * 20);
      params.append('limit', 20);
      
      const response = await api.get(`/admin/incidents?${params}`);
      setIncidents(response.data.incidents);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Error al cargar incidencias');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter, searchQuery, page]);
  
  useEffect(() => {
    fetchIncidents();
  }, [fetchIncidents]);
  
  const fetchIncidentDetail = async (id) => {
    try {
      const response = await api.get(`/admin/incidents/${id}`);
      setSelectedIncident(response.data);
    } catch (error) {
      toast.error('Error al cargar detalles');
    }
  };
  
  const updateIncident = async (updates) => {
    if (!selectedIncident) return;
    
    setUpdating(true);
    try {
      await api.patch(`/admin/incidents/${selectedIncident.id}`, updates);
      toast.success('Incidencia actualizada');
      fetchIncidentDetail(selectedIncident.id);
      fetchIncidents();
    } catch (error) {
      toast.error('Error al actualizar');
    } finally {
      setUpdating(false);
    }
  };
  
  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    await updateIncident({ note: newNote });
    setNewNote('');
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Incident List View
  const IncidentsList = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-red-500" />
            Incidencias
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            {total} incidencias en total
          </p>
        </div>
      </div>
      
      {/* Filters */}
      <div className="bg-[#1a1a1a] rounded-xl p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar por nombre, email o código..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#0a0a0a] border-gray-700"
                data-testid="incidents-search"
              />
            </div>
          </div>
          
          <Select value={statusFilter || "ALL"} onValueChange={(v) => setStatusFilter(v === "ALL" ? "" : v)}>
            <SelectTrigger className="w-[180px] bg-[#0a0a0a] border-gray-700" data-testid="status-filter">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todos</SelectItem>
              <SelectItem value="NEW">Nueva</SelectItem>
              <SelectItem value="IN_REVIEW">En revisión</SelectItem>
              <SelectItem value="RESOLVED">Resuelta</SelectItem>
              <SelectItem value="CLOSED">Cerrada</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={priorityFilter || "ALL"} onValueChange={(v) => setPriorityFilter(v === "ALL" ? "" : v)}>
            <SelectTrigger className="w-[180px] bg-[#0a0a0a] border-gray-700" data-testid="priority-filter">
              <SelectValue placeholder="Prioridad" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todas</SelectItem>
              <SelectItem value="HIGH">Alta</SelectItem>
              <SelectItem value="MEDIUM">Media</SelectItem>
              <SelectItem value="LOW">Baja</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Incidents Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
        </div>
      ) : incidents.length === 0 ? (
        <div className="text-center py-12 bg-[#1a1a1a] rounded-xl">
          <AlertTriangle className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">No hay incidencias</p>
        </div>
      ) : (
        <div className="bg-[#1a1a1a] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left p-4 text-gray-400 font-medium">Prioridad</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Cliente</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Reserva</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Valoración</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Estado</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Fecha</th>
                  <th className="text-right p-4"></th>
                </tr>
              </thead>
              <tbody>
                {incidents.map((incident) => {
                  const priority = PRIORITY_CONFIG[incident.priority] || PRIORITY_CONFIG.MEDIUM;
                  const status = STATUS_CONFIG[incident.status] || STATUS_CONFIG.NEW;
                  
                  return (
                    <tr
                      key={incident.id}
                      className="border-b border-gray-800/50 hover:bg-[#252525] transition-colors cursor-pointer"
                      onClick={() => fetchIncidentDetail(incident.id)}
                      data-testid={`incident-row-${incident.id}`}
                    >
                      <td className="p-4">
                        <span className="text-lg">{priority.icon}</span>
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="text-white font-medium">{incident.customer_name}</p>
                          <p className="text-gray-500 text-sm">{incident.customer_email}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="font-mono text-[#D4AF37]">{incident.reservation_code}</span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`w-4 h-4 ${
                                star <= incident.rating
                                  ? 'fill-[#D4AF37] text-[#D4AF37]'
                                  : 'text-gray-600'
                              }`}
                            />
                          ))}
                        </div>
                      </td>
                      <td className="p-4">
                        <Badge className={`${status.color} text-white`}>
                          {status.label}
                        </Badge>
                      </td>
                      <td className="p-4 text-gray-400 text-sm">
                        {formatDate(incident.created_at)}
                      </td>
                      <td className="p-4 text-right">
                        <ChevronRight className="w-5 h-5 text-gray-500" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {total > 20 && (
            <div className="flex justify-between items-center p-4 border-t border-gray-800">
              <Button
                variant="outline"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="border-gray-700"
              >
                Anterior
              </Button>
              <span className="text-gray-400">
                Página {page + 1} de {Math.ceil(total / 20)}
              </span>
              <Button
                variant="outline"
                onClick={() => setPage(p => p + 1)}
                disabled={(page + 1) * 20 >= total}
                className="border-gray-700"
              >
                Siguiente
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
  
  // Incident Detail View
  const IncidentDetail = () => {
    if (!selectedIncident) return null;
    
    const priority = PRIORITY_CONFIG[selectedIncident.priority] || PRIORITY_CONFIG.MEDIUM;
    const status = STATUS_CONFIG[selectedIncident.status] || STATUS_CONFIG.NEW;
    const responses = selectedIncident.responses || {};
    
    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        className="space-y-6"
      >
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => setSelectedIncident(null)}
            className="text-gray-400 hover:text-white"
            data-testid="back-to-list"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Volver
          </Button>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-white">
              Incidencia #{selectedIncident.id.slice(0, 8)}
            </h2>
          </div>
          <Badge className={`${priority.color} text-white`}>
            {priority.icon} {priority.label}
          </Badge>
          <Badge className={`${status.color} text-white`}>
            {status.label}
          </Badge>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer Info */}
            <div className="bg-[#1a1a1a] rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-[#D4AF37]" />
                Información del Cliente
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-500 text-sm">Nombre</p>
                  <p className="text-white font-medium">{selectedIncident.customer_name}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Reserva</p>
                  <p className="font-mono text-[#D4AF37]">{selectedIncident.reservation_code}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Email</p>
                  <p className="text-white">{selectedIncident.customer_email}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Teléfono</p>
                  <p className="text-white">{selectedIncident.customer_phone || '-'}</p>
                </div>
              </div>
              
              {/* Contact Buttons */}
              <div className="flex gap-3 mt-4 pt-4 border-t border-gray-800">
                <Button
                  variant="outline"
                  className="border-gray-700"
                  onClick={() => window.location.href = `mailto:${selectedIncident.customer_email}`}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Email
                </Button>
                {selectedIncident.customer_phone && (
                  <Button
                    variant="outline"
                    className="border-green-700 text-green-500 hover:bg-green-500/10"
                    onClick={() => window.open(`https://wa.me/34${selectedIncident.customer_phone.replace(/\s/g, '')}`, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    WhatsApp
                  </Button>
                )}
              </div>
            </div>
            
            {/* Survey Responses */}
            <div className="bg-[#1a1a1a] rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#D4AF37]" />
                Respuestas de la Encuesta
              </h3>
              
              {/* Rating */}
              <div className="mb-4">
                <p className="text-gray-500 text-sm mb-2">Valoración general</p>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`w-6 h-6 ${
                        star <= selectedIncident.rating
                          ? 'fill-[#D4AF37] text-[#D4AF37]'
                          : 'text-gray-600'
                      }`}
                    />
                  ))}
                  <span className="text-white ml-2 text-lg font-bold">
                    {selectedIncident.rating}/5
                  </span>
                </div>
              </div>
              
              {/* Yes/No Answers */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                <div className={`p-4 rounded-lg ${responses.delivery_ok ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                  <p className="text-gray-400 text-sm mb-1">Entrega/Recogida</p>
                  <div className="flex items-center gap-2">
                    {responses.delivery_ok ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )}
                    <span className={responses.delivery_ok ? 'text-green-500' : 'text-red-500'}>
                      {responses.delivery_ok ? 'Sí' : 'No'}
                    </span>
                  </div>
                </div>
                
                <div className={`p-4 rounded-lg ${responses.professional ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                  <p className="text-gray-400 text-sm mb-1">Trato profesional</p>
                  <div className="flex items-center gap-2">
                    {responses.professional ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )}
                    <span className={responses.professional ? 'text-green-500' : 'text-red-500'}>
                      {responses.professional ? 'Sí' : 'No'}
                    </span>
                  </div>
                </div>
                
                <div className={`p-4 rounded-lg ${responses.would_return ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                  <p className="text-gray-400 text-sm mb-1">Volvería a usar</p>
                  <div className="flex items-center gap-2">
                    {responses.would_return ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )}
                    <span className={responses.would_return ? 'text-green-500' : 'text-red-500'}>
                      {responses.would_return ? 'Sí' : 'No'}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Comment */}
              {selectedIncident.comment && (
                <div className="bg-[#0a0a0a] rounded-lg p-4">
                  <p className="text-gray-500 text-sm mb-2">Comentario del cliente</p>
                  <p className="text-white italic">"{selectedIncident.comment}"</p>
                </div>
              )}
            </div>
            
            {/* History */}
            <div className="bg-[#1a1a1a] rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#D4AF37]" />
                Historial
              </h3>
              <div className="space-y-4">
                {(selectedIncident.history || []).map((entry, idx) => (
                  <div key={`history-${entry.timestamp}-${idx}`} className="flex gap-4 text-sm">
                    <div className="w-2 h-2 rounded-full bg-[#D4AF37] mt-2" />
                    <div>
                      <p className="text-gray-400">{formatDate(entry.timestamp)}</p>
                      <p className="text-white">{entry.action}</p>
                      {entry.details && <p className="text-gray-500">{entry.details}</p>}
                      <p className="text-gray-600 text-xs">Por: {entry.by}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Sidebar Actions */}
          <div className="space-y-6">
            {/* Status Update */}
            <div className="bg-[#1a1a1a] rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Actualizar Estado</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Estado</label>
                  <Select
                    value={selectedIncident.status}
                    onValueChange={(value) => updateIncident({ status: value })}
                    disabled={updating}
                  >
                    <SelectTrigger className="bg-[#0a0a0a] border-gray-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NEW">Nueva</SelectItem>
                      <SelectItem value="IN_REVIEW">En revisión</SelectItem>
                      <SelectItem value="RESOLVED">Resuelta</SelectItem>
                      <SelectItem value="CLOSED">Cerrada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Prioridad</label>
                  <Select
                    value={selectedIncident.priority}
                    onValueChange={(value) => updateIncident({ priority: value })}
                    disabled={updating}
                  >
                    <SelectTrigger className="bg-[#0a0a0a] border-gray-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="HIGH">Alta</SelectItem>
                      <SelectItem value="MEDIUM">Media</SelectItem>
                      <SelectItem value="LOW">Baja</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            
            {/* Add Note */}
            <div className="bg-[#1a1a1a] rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Añadir Nota</h3>
              <Textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Escribe una nota interna..."
                className="bg-[#0a0a0a] border-gray-700 mb-4"
                data-testid="add-note-input"
              />
              <Button
                onClick={handleAddNote}
                disabled={!newNote.trim() || updating}
                className="w-full bg-[#D4AF37] hover:bg-[#B8962E] text-black"
                data-testid="add-note-btn"
              >
                {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar nota'}
              </Button>
              
              {/* Notes List */}
              {(selectedIncident.notes || []).length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-800 space-y-3">
                  {selectedIncident.notes.map((note, idx) => (
                    <div key={`note-${note.at}-${idx}`} className="bg-[#0a0a0a] rounded-lg p-3">
                      <p className="text-white text-sm">{note.text}</p>
                      <p className="text-gray-500 text-xs mt-1">
                        {note.by} · {formatDate(note.at)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Quick Actions */}
            <div className="bg-[#1a1a1a] rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Acciones Rápidas</h3>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full border-green-700 text-green-500 hover:bg-green-500/10"
                  onClick={() => updateIncident({ status: 'RESOLVED' })}
                  disabled={updating || selectedIncident.status === 'RESOLVED'}
                  data-testid="mark-resolved-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Marcar como resuelta
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-gray-700"
                  onClick={() => updateIncident({ status: 'CLOSED' })}
                  disabled={updating || selectedIncident.status === 'CLOSED'}
                >
                  Cerrar incidencia
                </Button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    );
  };
  
  return (
    <div className="p-6">
      <AnimatePresence mode="wait">
        {selectedIncident ? <IncidentDetail key="detail" /> : <IncidentsList key="list" />}
      </AnimatePresence>
    </div>
  );
};

export default IncidentsManager;
